import { Col, Radio, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import {
  closeExemptionNotification,
  sendExemptionNotification
} from './exemptionNotification';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import useQuotesContext from './QuotesContext';
import numeral from 'numeral';
import CollapsableText from '../../utils/CollapsableText';
import FormRadioButtonWithCustom from '../../components/FormRadioButtonWithCustom/FormRadioButtonWithCustom';
import { amendmentPurposes } from './QuotesRouter';
import { MARKEL_EXEMPTED_ZIPCODES } from '../../utils/markel-exemptions-zipcodes';
import { EXCLUSION_EXEMPTION_ITEM } from './checkExemption';

//  TURNOVER
export const TURNOVER_EXEMPTION_THRESHOLD = 25000001;
const turnoverSteps = [
  {
    value: 1500000,
    label: '0€ à 1 500 000€'
  },
  {
    value: 5000000,
    label: '1 500 000€ à 5 000 000€'
  },
  {
    value: 15000000,
    label: '5 000 000€ à 15 000 000€'
  },
  {
    value: 25000000,
    label: '15 000 000€ à 25 000 000€'
  },
  {
    value: TURNOVER_EXEMPTION_THRESHOLD,
    label: '+ de 25 000 000 €'
  }
];
export const TURNOVER_DEFAULT_VALUE = turnoverSteps[0].value;

// GUARANTEE LIMIT
export const GUARANTEE_LIMIT_EXEMPTION_THRESHOLD = 2500001;
const guaranteeLimitSteps = [
  {
    value: 100000,
    label: '100 000€'
  },
  {
    value: 250000,
    label: '250 000€'
  },
  {
    value: 500000,
    label: '500 000€'
  },
  {
    value: 1000000,
    label: '1 000 000€'
  },
  {
    value: 1500000,
    label: '1 500 000€'
  },
  {
    value: 2500000,
    label: '2 500 000€'
  },
  {
    value: 2500001,
    label: '+ de 2 500 000€'
  }
];
export const GUARANTEE_LIMIT_DEFAULT_VALUE = guaranteeLimitSteps[0].value;

export const DO_EXEMPTED_ZIPCODES = MARKEL_EXEMPTED_ZIPCODES;

const useDOFields = (form, purpose) => {
  const { t } = useTranslation();

  const { exemptions, formValues } = useQuotesContext();

  const [previousExemptionsLength, setPreviousExemptionsLength] = useState(
    exemptions.length
  );

  const isAmendment = useMemo(() => window.location.href.search('amendment'), [
    window.location.href
  ]);

  useEffect(() => {
    const length = exemptions.length;

    if (length > previousExemptionsLength && length === 1) {
      if (!exemptions.includes(EXCLUSION_EXEMPTION_ITEM)) {
        sendExemptionNotification();
      }
    } else if (length === 0) {
      closeExemptionNotification();
    }

    setPreviousExemptionsLength(exemptions.length);
  }, [exemptions.length]);

  let doFields = [
    /*
     * STEP 1
     */
    {
      name: ['turnover'],
      tooltip: true,
      tooltipLabel: t('contracts.form.tooltip.do.turnover', {
        threshold: numeral(TURNOVER_EXEMPTION_THRESHOLD - 1).format()
      }),
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            formValues && value > 0
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'))
        }
      ],
      input: (
        <FormRadioButtonWithCustom
          fieldName="turnover"
          options={turnoverSteps}
          getIsCustom={({ value }) =>
            value >= TURNOVER_EXEMPTION_THRESHOLD &&
            formValues?.turnover >= TURNOVER_EXEMPTION_THRESHOLD
          }
          customInputProps={{
            min: TURNOVER_EXEMPTION_THRESHOLD,
            max: null
          }}
        />
      ),
      help: exemptions.includes('turnover') ? t('help.exemptions') : null,
      step: 0
    },
    {
      name: ['guarantee_limit'],
      tooltip: true,
      tooltipLabel: t('contracts.form.tooltip.do.guarantee_limit', {
        threshold: numeral(GUARANTEE_LIMIT_EXEMPTION_THRESHOLD - 1).format()
      }),
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            formValues && value > 0
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'))
        }
      ],
      input: (
        <FormRadioButtonWithCustom
          fieldName="guarantee_limit"
          options={guaranteeLimitSteps}
          getIsCustom={({ value }) =>
            value >= GUARANTEE_LIMIT_EXEMPTION_THRESHOLD &&
            formValues?.guarantee_limit >= GUARANTEE_LIMIT_EXEMPTION_THRESHOLD
          }
          customInputProps={{
            min: GUARANTEE_LIMIT_EXEMPTION_THRESHOLD,
            max: null
          }}
        />
      ),
      help: exemptions.includes('guarantee_limit')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    {
      name: ['employer_protection'],
      input: <PreRequisitesSwitch />,
      label: (
        <Col>
          <CollapsableText
            title={
              <Trans>
                <Typography.Title level={5}>
                  {t(`contracts.form.employer_protection`)}
                </Typography.Title>
              </Trans>
            }
            text={
              <Trans>
                <Col>
                  <Typography.Paragraph style={{ textTransform: 'none' }}>
                    {t(`contracts.form.employer_protection_description`)}
                  </Typography.Paragraph>
                  <Typography.Text style={{ textTransform: 'none' }}>
                    {t(`contracts.form.employer_protection_helper`)}
                  </Typography.Text>
                </Col>
              </Trans>
            }
          />
        </Col>
      ),
      formattedLabel: true,
      labelAlign: 'left',
      valuePropName: 'checked',
      withDivider: true,
      step: 0
    },
    {
      name: ['cyber_protection'],
      input: <PreRequisitesSwitch />,
      label: (
        <Col>
          <CollapsableText
            title={
              <Trans>
                <Typography.Title level={5}>
                  {t(`contracts.form.cyber_protection`)}
                </Typography.Title>
              </Trans>
            }
            text={
              <Trans>
                <Col>
                  <Typography.Paragraph style={{ textTransform: 'none' }}>
                    {t(`contracts.form.cyber_protection_description`)}
                  </Typography.Paragraph>
                  <Typography.Text style={{ textTransform: 'none' }}>
                    {t(`contracts.form.cyber_protection_helper`)}
                  </Typography.Text>
                </Col>
              </Trans>
            }
          />
        </Col>
      ),
      formattedLabel: true,
      labelAlign: 'left',
      valuePropName: 'checked',
      withDivider: true,
      step: 0
    },
    {
      name: ['holding_protection'],
      input: <PreRequisitesSwitch />,
      label: (
        <Col>
          <CollapsableText
            title={
              <Trans>
                <Typography.Title level={5}>
                  {t(`contracts.form.holding_protection`)}
                </Typography.Title>
              </Trans>
            }
            text={
              <Trans>
                <Col>
                  <Typography.Paragraph style={{ textTransform: 'none' }}>
                    {t(`contracts.form.holding_protection_description`)}
                  </Typography.Paragraph>
                  <Typography.Text style={{ textTransform: 'none' }}>
                    {t(`contracts.form.holding_protection_helper`)}
                  </Typography.Text>
                </Col>
              </Trans>
            }
          />
        </Col>
      ),
      formattedLabel: true,
      labelAlign: 'left',
      valuePropName: 'checked',
      withDivider: true,
      step: 0
    },
    {
      name: ['key_person_protection'],
      input: <PreRequisitesSwitch />,
      label: (
        <Col>
          <CollapsableText
            title={
              <Trans>
                <Typography.Title level={5}>
                  {t(`contracts.form.key_person_protection`)}
                </Typography.Title>
              </Trans>
            }
            text={
              <Trans>
                <Col>
                  <Typography.Paragraph style={{ textTransform: 'none' }}>
                    {t(`contracts.form.key_person_protection_description`)}
                  </Typography.Paragraph>
                  <Typography.Text style={{ textTransform: 'none' }}>
                    {t(`contracts.form.key_person_protection_helper`)}
                  </Typography.Text>
                </Col>
              </Trans>
            }
          />
        </Col>
      ),
      formattedLabel: true,
      labelAlign: 'left',
      valuePropName: 'checked',
      withDivider: true,
      step: 0
    },
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: false,
      input: (
        <Radio.Group buttonStyle="solid">
          <Radio.Button value="MONTHLY">
            {t('contracts.form.options.MONTHLY')}
          </Radio.Button>
          <Radio.Button value="QUARTERLY">
            {t('contracts.form.options.QUARTERLY')}
          </Radio.Button>
          <Radio.Button value="BIANNUAL">
            {t('contracts.form.options.BIANNUAL')}
          </Radio.Button>
          <Radio.Button value="YEARLY">
            {t('contracts.form.options.YEARLY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0,
      show: !amendmentPurposes.includes(purpose)
    },
    {
      name: ['payment_type'],
      label: 'payment_type',
      rules: [{ required: true }],
      input: (
        <Radio.Group buttonStyle="solid">
          <Radio.Button value="DIRECTDEBIT">
            {t('contracts.form.options.DIRECTDEBIT')}
          </Radio.Button>
          {formValues && formValues.fraction_type !== 'MONTHLY' && (
            <Radio.Button value="CARD">
              {t('contracts.form.options.CARD')}
            </Radio.Button>
          )}
        </Radio.Group>
      ),
      tooltip: true,
      step: 0,
      show: !amendmentPurposes.includes(purpose)
    },

    /*
     * STEP 2
     */

    {
      name: ['max_tax_result'],
      step: 1
    },
    {
      name: ['metropolitan_france'],
      step: 1
    },
    {
      name: ['more_than_2_years'],
      step: 1
    },
    {
      name: ['no_merge_takeover'],
      step: 1
    },
    {
      name: ['stock_exchange_listing'],
      step: 1
    },
    {
      name: ['insolvency_proceeding'],
      step: 1
    },
    {
      name: ['positive_last_2_tax_result'],
      step: 1
    },
    {
      name: ['positive_last_2_equity'],
      step: 1
    },
    {
      name: ['last_5_years_blame'],
      step: 1
    },
    {
      name: ['not_operate_in_forbidden_activities'],
      label: (
        <Col>
          <label>
            {t('contracts.form.not_operate_in_forbidden_activities')}
          </label>
          <ul>
            {t('contracts.form.do_forbidden_activities', {
              returnObjects: true,
              defaultValue: []
            }).map((activity) => (
              <li key={activity}>{activity}</li>
            ))}
          </ul>
        </Col>
      ),
      blocked_activities: true,
      step: 1
    },
    {
      name: ['holding_prerequisites'],
      hidden: !formValues?.holding_protection,
      label: (
        <Trans>
          <p>{t('contracts.form.holding_prerequisites')}</p>
        </Trans>
      ),
      formattedLabel: true,
      step: 1
    }
  ];

  // FIELDS FORMATTER BY STEP
  doFields = doFields.map((field) => {
    if (field.step === 1) {
      return {
        input: <PreRequisitesSwitch />,
        labelAlign: 'left',
        valuePropName: 'checked',
        withDivider: true,
        layout: preRequisitesLayout,
        ...field
      };
    }

    return field;
  });

  return {
    doFields
  };
};

export default useDOFields;
